import { heroImage, price } from '../data/copy'

const TITLE =
  'Yoga Teaching Vault™ — 120 Ready-to-Teach Yoga Classes for Teachers'
const DESCRIPTION =
  'A complete ready-to-teach yoga class system: 6 teaching volumes with 120 classes (Beginner, Hatha, Slow Flow, Mobility, Restore, Chair & Mixed-Level) plus 7 practical teacher bonuses. 100% digital PDFs, instant access.'
const SITE_NAME = 'Yoga Teaching Vault™'

function upsertMeta(
  attribute: 'name' | 'property',
  key: string,
  content: string,
): void {
  let tag = document.head.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${key}"]`,
  )
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, key)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

function upsertLink(rel: string, href: string, extra?: Record<string, string>) {
  let tag = document.head.querySelector<HTMLLinkElement>(
    `link[rel="${rel}"][data-managed="true"]`,
  )
  if (!tag) {
    tag = document.createElement('link')
    tag.rel = rel
    tag.dataset.managed = 'true'
    document.head.appendChild(tag)
  }
  tag.href = href
  if (extra) {
    Object.entries(extra).forEach(([name, value]) =>
      tag?.setAttribute(name, value),
    )
  }
  return tag
}

/** Loads the Google Fonts stylesheet without blocking the first paint. */
function loadFontsAsync(): void {
  const preconnects = [
    'https://fonts.googleapis.com',
    'https://fonts.gstatic.com',
  ]
  preconnects.forEach((href) => {
    if (document.head.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
      return
    }
    const link = document.createElement('link')
    link.rel = 'preconnect'
    link.href = href
    if (href.includes('gstatic')) link.crossOrigin = ''
    document.head.appendChild(link)
  })

  const fontsHref =
    'https://fonts.googleapis.com/css2?family=Marcellus&family=Inter:wght@400;500;600;700&display=swap'
  if (document.head.querySelector(`link[href="${fontsHref}"]`)) return

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = fontsHref
  // Non-blocking pattern: fetch at low priority, then apply to all media.
  link.media = 'print'
  link.onload = () => {
    link.media = 'all'
  }
  document.head.appendChild(link)
}

/** Preloads the hero mockup so it becomes the LCP element as early as possible. */
function preloadHeroImage(): void {
  if (document.head.querySelector(`link[rel="preload"][href="${heroImage}"]`)) {
    return
  }
  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'image'
  link.href = heroImage
  link.setAttribute('fetchpriority', 'high')
  document.head.appendChild(link)
}

function addStructuredData(): void {
  if (document.head.querySelector('script[data-schema="product"]')) return
  const script = document.createElement('script')
  script.type = 'application/ld+json'
  script.dataset.schema = 'product'
  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: SITE_NAME,
    description: DESCRIPTION,
    image: heroImage,
    brand: { '@type': 'Brand', name: SITE_NAME },
    offers: {
      '@type': 'Offer',
      price: price.replace('$', ''),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
    },
  })
  document.head.appendChild(script)
}

/**
 * Sets the document metadata (title, description, social cards, canonical,
 * structured data) and kicks off the non-blocking font + hero image loads.
 */
export function setupHead(): void {
  if (typeof document === 'undefined') return

  document.documentElement.lang = 'en'
  document.title = TITLE

  upsertMeta('name', 'description', DESCRIPTION)
  upsertMeta('name', 'robots', 'index, follow')
  upsertMeta('name', 'theme-color', '#FCF9F4')

  upsertMeta('property', 'og:type', 'product')
  upsertMeta('property', 'og:site_name', SITE_NAME)
  upsertMeta('property', 'og:title', TITLE)
  upsertMeta('property', 'og:description', DESCRIPTION)
  upsertMeta('property', 'og:image', heroImage)
  upsertMeta('property', 'og:url', window.location.href)

  upsertMeta('name', 'twitter:card', 'summary_large_image')
  upsertMeta('name', 'twitter:title', TITLE)
  upsertMeta('name', 'twitter:description', DESCRIPTION)
  upsertMeta('name', 'twitter:image', heroImage)

  upsertLink('canonical', window.location.origin + window.location.pathname)

  preloadHeroImage()
  loadFontsAsync()
  addStructuredData()
}
